import { hasChanged, hasOwn, isArray, isObject, isString, isSymbol, warn } from "@lune-js/utils";
//#region src/debug.ts
const triggerEventInfos = [];
function onTrack(sub, debugInfo) {
	if (!import.meta.env.DEV) throw new Error(`Internal error: onTrack should be called only in development.`);
	if (sub.onTrack) sub.onTrack(Object.assign({ effect: sub }, debugInfo));
}
function onTrigger(sub) {
	if (!import.meta.env.DEV) throw new Error(`Internal error: onTrigger should be called only in development.`);
	if (sub.onTrigger) {
		const debugInfo = triggerEventInfos[triggerEventInfos.length - 1];
		sub.onTrigger(Object.assign({ effect: sub }, debugInfo));
	}
}
function setupOnTrigger(target) {
	if (!import.meta.env.DEV) throw new Error(`Internal error: setupOnTrigger should be called only in development.`);
	Object.defineProperty(target.prototype, "onTrigger", {
		get() {
			return this._onTrigger;
		},
		set(val) {
			if (val && !this._onTrigger) setupFlagsHandler(this);
			this._onTrigger = val;
		}
	});
}
function setupFlagsHandler(target) {
	target._flags = target.flags;
	Object.defineProperty(target, "flags", {
		get() {
			return target._flags;
		},
		set(value) {
			if (!(target._flags & 24) && !!(value & 24)) onTrigger(this);
			target._flags = value;
		}
	});
}
//#endregion
//#region src/system.ts
const notifyBuffer = [];
let batchDepth = 0;
let activeSub = void 0;
let runDepth = 0;
function incRunDepth() {
	++runDepth;
}
function decRunDepth() {
	--runDepth;
}
let globalVersion = 0;
let notifyIndex = 0;
let notifyBufferLength = 0;
function setActiveSub(sub) {
	try {
		return activeSub;
	} finally {
		activeSub = sub;
	}
}
/**
* Opens a batch block window. State modifications during a batch defer synchronous effect flushes
* until the parent batch context reaches completion.
*/
function startBatch() {
	++batchDepth;
}
/**
* Decrements the current batch window scale depth, executing and flushing all queued dirty subscriber nodes
* once the outermost batch block closes down completely.
*/
function endBatch() {
	if (!--batchDepth && notifyBufferLength) flush();
}
function link(dep, sub) {
	const prevDep = sub.depsTail;
	if (prevDep !== void 0 && prevDep.dep === dep) return;
	const nextDep = prevDep !== void 0 ? prevDep.nextDep : sub.deps;
	if (nextDep !== void 0 && nextDep.dep === dep) {
		nextDep.version = globalVersion;
		sub.depsTail = nextDep;
		return;
	}
	const prevSub = dep.subsTail;
	if (prevSub !== void 0 && prevSub.version === globalVersion && prevSub.sub === sub) return;
	const newLink = sub.depsTail = dep.subsTail = {
		version: globalVersion,
		dep,
		sub,
		prevDep,
		nextDep,
		prevSub,
		nextSub: void 0
	};
	if (nextDep !== void 0) nextDep.prevDep = newLink;
	if (prevDep !== void 0) prevDep.nextDep = newLink;
	else sub.deps = newLink;
	if (prevSub !== void 0) prevSub.nextSub = newLink;
	else dep.subs = newLink;
}
function unlink(link, sub = link.sub) {
	const dep = link.dep;
	const prevDep = link.prevDep;
	const nextDep = link.nextDep;
	const nextSub = link.nextSub;
	const prevSub = link.prevSub;
	if (nextDep !== void 0) nextDep.prevDep = prevDep;
	else sub.depsTail = prevDep;
	if (prevDep !== void 0) prevDep.nextDep = nextDep;
	else sub.deps = nextDep;
	if (nextSub !== void 0) nextSub.prevSub = prevSub;
	else dep.subsTail = prevSub;
	if (prevSub !== void 0) prevSub.nextSub = nextSub;
	else dep.subs = nextSub;
	return nextDep;
}
/**
* Walks the subscribers of a changed dependency, marking each one pending and queueing it for
* the next flush. A subscriber that is already queued, or that is only re-entering itself from
* inside its own run, is marked but not queued.
*/
function propagate(link) {
	do {
		const sub = link.sub;
		const nextSub = link.nextSub;
		const flags = sub.flags;
		if (flags & 1) {
			let notify = true;
			if (!(flags & 30)) {
				sub.flags = flags | 16;
				if (runDepth) sub.flags |= 4;
			} else if (!(flags & 6)) notify = false;
			else if (!(flags & 2)) sub.flags = flags & -5 | 16;
			else if (!(flags & 24) && isValidLink(link, sub)) {
				sub.flags = flags | 20;
				notify = false;
			} else notify = false;
			if (notify) notifyBuffer[notifyBufferLength++] = sub;
		}
		link = nextSub;
	} while (link !== void 0);
}
function startTracking(sub) {
	++globalVersion;
	sub.depsTail = void 0;
	sub.flags = sub.flags & -29 | 2;
	return setActiveSub(sub);
}
function endTracking(sub, prevSub) {
	if (import.meta.env.DEV && activeSub !== sub) warn("Active effect was not restored correctly - this is likely a internal bug in Lune.");
	activeSub = prevSub;
	const depsTail = sub.depsTail;
	let toRemove = depsTail !== void 0 ? depsTail.nextDep : sub.deps;
	while (toRemove !== void 0) toRemove = unlink(toRemove, sub);
	sub.flags &= -3;
}
function flush() {
	while (notifyIndex < notifyBufferLength) {
		const effect = notifyBuffer[notifyIndex];
		notifyBuffer[notifyIndex++] = void 0;
		effect.notify();
	}
	notifyIndex = 0;
	notifyBufferLength = 0;
}
function shallowPropagate(link) {
	do {
		const sub = link.sub;
		const nextSub = link.nextSub;
		const subFlags = sub.flags;
		if ((subFlags & 24) === 16) sub.flags = subFlags | 8;
		link = nextSub;
	} while (link !== void 0);
}
function isValidLink(checkLink, sub) {
	let link = sub.depsTail;
	while (link !== void 0) {
		if (link === checkLink) return true;
		link = link.prevDep;
	}
	return false;
}
//#endregion
//#region src/effect.ts
/**
* Core representation of a graph tracking node managing reactive side effects.
* Manages links between dependency maps and executing subscribers.
*/
var ReactiveEffect = class {
	deps = void 0;
	depsTail = void 0;
	flags = 9;
	/**
	* @internal
	*/
	cleanups = [];
	/**
	* @internal
	*/
	cleanupsLength = 0;
	fn() {}
	constructor(fn) {
		if (fn !== void 0) this.fn = fn;
	}
	get active() {
		return !(this.flags & 1024);
	}
	/**
	* Pauses property update notifications from propagating into this effect runner.
	*/
	pause() {
		this.flags |= 256;
	}
	/**
	* Resumes and flushes deferred updates, synchronously evaluating side effects if dependencies became dirty.
	*/
	resume() {
		if ((this.flags &= -257) & 24) this.notify();
	}
	notify() {
		if (!(this.flags & 256) && this.dirty) this.run();
	}
	/**
	* Triggers evaluation tracking hooks, establishing this node as the active global subscriber.
	*/
	run() {
		if (!this.active) return this.fn();
		cleanup(this);
		const prevSub = startTracking(this);
		incRunDepth();
		try {
			return this.fn();
		} finally {
			decRunDepth();
			endTracking(this, prevSub);
			const flags = this.flags;
			if ((flags & 132) === 132) {
				this.flags = flags & -5;
				this.notify();
			}
		}
	}
	/**
	* severs all data-graph associations for this effect context, preventing any future tracking executions.
	*/
	stop() {
		if (!this.active) return;
		this.flags = 1024;
		let dep = this.deps;
		while (dep !== void 0) dep = unlink(dep, this);
		cleanup(this);
	}
	/**
	* `trigger` always pairs `propagate` with `shallowPropagate` over the same subscriber list, so
	* every subscriber left pending is promoted to dirty before it is ever notified. `Pending`
	* therefore never outlives `Dirty` here, and this only has to read the one flag.
	*/
	get dirty() {
		return !!(this.flags & 8);
	}
};
if (import.meta.env.DEV) setupOnTrigger(ReactiveEffect);
function cleanup(sub) {
	const l = sub.cleanupsLength;
	if (l) {
		for (let i = 0; i < l; i++) sub.cleanups[i]();
		sub.cleanupsLength = 0;
	}
}
/**
* Wraps a side-effect function inside a tracking context (`ReactiveEffect`).
* Automatically invokes the side-effect immediately to trace dependency properties,
* subsequently re-running the function whenever tracked proxy states mutate.
* @param fn - The execution function containing reactive properties.
* @param options - Configurable hooks, such as a custom execution `scheduler` or dev-only `onTrack` / `onTrigger` debug triggers.
* @returns The runner function linked to the internal `ReactiveEffect` node instance.
*/
function effect(fn, options) {
	if (fn.effect instanceof ReactiveEffect) fn = fn.effect.fn;
	const e = new ReactiveEffect(fn);
	if (options) {
		const { onStop, scheduler } = options;
		if (onStop) {
			options.onStop = void 0;
			const stop = e.stop.bind(e);
			e.stop = () => {
				stop();
				onStop();
			};
		}
		if (scheduler) {
			options.scheduler = void 0;
			e.notify = () => {
				if (!(e.flags & 256)) scheduler();
			};
		}
		Object.assign(e, options);
	}
	try {
		e.run();
	} catch (err) {
		e.stop();
		throw err;
	}
	const runner = e.run.bind(e);
	runner.effect = e;
	return runner;
}
/**
* Stops the effect associated with the given runner.
*
* @param runner - Association with the effect to stop tracking.
*/
function stop(runner) {
	runner.effect.stop();
}
//#endregion
//#region src/utils.ts
/**
* Checks if `key` is an integer key.
*
* This function is taken from Vue's source code:
* https://github.com/vuejs/core/blob/v3.5.34/packages/shared/src/general.ts
*
* @param {unknown} key The value to check if it is an integer key.
* @returns {boolean} Returns `true` if `key` is an integer key, else `false`.
*
* @example
* console.log(isIntegerKey('0')); // true
* console.log(isIntegerKey('1')); // true
* console.log(isIntegerKey('-1')); // false
* console.log(isIntegerKey('NaN')); // false
* console.log(isIntegerKey('1.5')); // false
* console.log(isIntegerKey('abc')); // false
* console.log(isIntegerKey(0)); // false
*/
function isIntegerKey(key) {
	return isString(key) && key !== "NaN" && key[0] !== "-" && "" + parseInt(key, 10) === key;
}
/**
* Checks if an object is a proxy created by {@link reactive},
* {@link readonly}, {@link shallowReactive} or {@link shallowReadonly}.
*
* @param value - The value to check.
* @see {@link https://vuejs.org/api/reactivity-utilities.html#isproxy}
*/
/*@__NO_SIDE_EFFECTS__*/
function isProxy(value) {
	return value ? !!value["__lu_raw"] : false;
}
/**
* Checks if an object is a proxy created by {@link reactive} or
* {@link shallowReactive} (or {@link ref} in some cases).
*
* @example
* ```js
* isReactive(reactive({}))            // => true
* isReactive(readonly(reactive({})))  // => true
* isReactive(ref({}).value)           // => true
* isReactive(readonly(ref({})).value) // => true
* isReactive(ref(true))               // => false
* isReactive(shallowRef({}).value)    // => false
* isReactive(shallowReactive({}))     // => true
* ```
*
* @param value - The value to check.
* @see {@link https://vuejs.org/api/reactivity-utilities.html#isreactive}
*/
/*@__NO_SIDE_EFFECTS__*/
function isReactive(value) {
	if (/* @__PURE__ */ isReadonly(value)) return /* @__PURE__ */ isReactive(value["__lu_raw"]);
	return !!(value && value["__lu_isReactive"]);
}
/**
* Checks whether the passed value is a readonly object. The properties of a
* readonly object can change, but they can't be assigned directly via the
* passed object.
*
* The proxies created by {@link readonly} and {@link shallowReadonly} are
* both considered readonly, as is a computed ref without a set function.
*
* @param value - The value to check.
* @see {@link https://vuejs.org/api/reactivity-utilities.html#isreadonly}
*/
/*@__NO_SIDE_EFFECTS__*/
function isReadonly(value) {
	return !!(value && value["__lu_isReadonly"]);
}
/*@__NO_SIDE_EFFECTS__*/
function isShallow(value) {
	return !!(value && value["__lu_isShallow"]);
}
/**
* Make a map and return a function for checking if a key
* is in that map.
* IMPORTANT: all calls of this function must be prefixed with
* \/\*#\_\_PURE\_\_\*\/
* So that rollup can tree-shake them if necessary.
*/
/*@__NO_SIDE_EFFECTS__*/
function makeMap(str) {
	const map = Object.create(null);
	for (const key of str.split(",")) map[key] = 1;
	return (val) => val in map;
}
/**
* Returns the raw, original object of a Lune-created proxy.
*
* `toRaw()` can return the original object from proxies created by
* {@link reactive}, {@link readonly}, {@link shallowReactive} or
* {@link shallowReadonly}.
*
* This is an escape hatch that can be used to temporarily read without
* incurring proxy access / tracking overhead or write without triggering
* changes. It is **not** recommended to hold a persistent reference to the
* original object. Use with caution.
*
* @example
* ```js
* const foo = {}
* const reactiveFoo = reactive(foo)
*
* console.log(toRaw(reactiveFoo) === foo) // true
* ```
*
* @param observed - The object for which the "raw" value is requested.
* @see {@link https://vuejs.org/api/reactivity-advanced.html#toraw}
*/
/*@__NO_SIDE_EFFECTS__*/
function toRaw(observed) {
	const raw = observed && observed["__lu_raw"];
	return raw ? /* @__PURE__ */ toRaw(raw) : observed;
}
function toRawType(value) {
	return Object.prototype.toString.call(value).slice(8, -1);
}
//#endregion
//#region src/dep.ts
var Dep = class {
	map;
	key;
	_subs = void 0;
	subsTail = void 0;
	flags = 0;
	constructor(map, key) {
		this.map = map;
		this.key = key;
	}
	get subs() {
		return this._subs;
	}
	set subs(value) {
		this._subs = value;
		if (value === void 0) this.map.delete(this.key);
	}
};
const targetMap = /* @__PURE__ */ new WeakMap();
const ITERATE_KEY = Symbol(import.meta.env.DEV ? "Object iterate" : "");
const ARRAY_ITERATE_KEY = Symbol(import.meta.env.DEV ? "Array iterate" : "");
/**
* Connects the active global `effect` subscriber context with a targeted object dependency property.
* Called inside proxy `get`, `has`, or iteration traps.
* @param target - The underlying raw object.
* @param type - The operation flavor (e.g., TrackOpTypes.GET).
* @param key - The property symbol or identifier string.
*/
function track(target, type, key) {
	if (activeSub !== void 0) {
		let depsMap = targetMap.get(target);
		if (!depsMap) targetMap.set(target, depsMap = /* @__PURE__ */ new Map());
		let dep = depsMap.get(key);
		if (!dep) depsMap.set(key, dep = new Dep(depsMap, key));
		if (import.meta.env.DEV) onTrack(activeSub, {
			target,
			type,
			key
		});
		link(dep, activeSub);
	}
}
/**
* Sweeps through registered graph links to evaluate changes and schedule runs for tracking subscribers.
* Triggered on proxy modifications like `set`, `add`, or `deleteProperty`.
* @param target - The target raw source tracking the update.
* @param type - The operation specification (e.g., TriggerOpTypes.SET).
* @param key - The mutation property indicator.
* @param newValue - The incoming property assignment.
* @param oldValue - The prior matching value.
*/
function trigger(target, type, key, newValue, oldValue, oldTarget) {
	const depsMap = targetMap.get(target);
	if (!depsMap) return;
	const run = (dep) => {
		if (dep !== void 0 && dep.subs !== void 0) {
			if (import.meta.env.DEV) triggerEventInfos.push({
				target,
				type,
				key,
				newValue,
				oldValue,
				oldTarget
			});
			propagate(dep.subs);
			shallowPropagate(dep.subs);
			if (import.meta.env.DEV) triggerEventInfos.pop();
		}
	};
	startBatch();
	if (type === "clear") depsMap.forEach(run);
	else {
		const targetIsArray = isArray(target);
		const isArrayIndex = targetIsArray && isIntegerKey(key);
		if (targetIsArray && key === "length") {
			const newLength = Number(newValue);
			depsMap.forEach((dep, key) => {
				if (key === "length" || key === ARRAY_ITERATE_KEY || !isSymbol(key) && key >= newLength) run(dep);
			});
		} else {
			if (key !== void 0 || depsMap.has(void 0)) run(depsMap.get(key));
			if (isArrayIndex) run(depsMap.get(ARRAY_ITERATE_KEY));
			switch (type) {
				case "add":
					if (!targetIsArray) run(depsMap.get(ITERATE_KEY));
					else if (isArrayIndex) run(depsMap.get("length"));
					break;
				case "delete": if (!targetIsArray) run(depsMap.get(ITERATE_KEY));
			}
		}
	}
	endBatch();
}
//#endregion
//#region src/arrayInstrumentations.ts
/**
* Track array iteration and return:
* - if input is reactive: a cloned raw array with reactive values
* - if input is non-reactive or shallowReactive: the original raw array
*/
function reactiveReadArray(array) {
	const raw = /* @__PURE__ */ toRaw(array);
	if (raw === array) return raw;
	track(raw, "iterate", ARRAY_ITERATE_KEY);
	return /* @__PURE__ */ isShallow(array) ? raw : raw.map(toReactive);
}
/**
* Track array iteration and return raw array
*/
function shallowReadArray(arr) {
	track(arr = /* @__PURE__ */ toRaw(arr), "iterate", ARRAY_ITERATE_KEY);
	return arr;
}
/**
* Returns a reactive proxy of the given value (if possible).
*
* If the given value is not an object, the original value itself is returned.
*
* @param value - The value for which a reactive proxy shall be created.
*/
function toReactive(value) {
	if (isObject(value)) return /* @__PURE__ */ reactive(value);
	return value;
}
/**
* Returns a readonly proxy of the given value (if possible).
*
* If the given value is not an object, the original value itself is returned.
*
* @param value - The value for which a readonly proxy shall be created.
*/
function toReadonly(value) {
	if (isObject(value)) return /* @__PURE__ */ readonly(value);
	return value;
}
function toWrapped(target, item) {
	if (/* @__PURE__ */ isReadonly(target)) return /* @__PURE__ */ isReactive(target) ? toReadonly(toReactive(item)) : toReadonly(item);
	return toReactive(item);
}
const arrayInstrumentations = {
	__proto__: null,
	[Symbol.iterator]() {
		return iterator(this, Symbol.iterator, (item) => toWrapped(this, item));
	},
	concat(...args) {
		return reactiveReadArray(this).concat(...args.map((x) => isArray(x) ? reactiveReadArray(x) : x));
	},
	entries() {
		return iterator(this, "entries", (value) => {
			value[1] = toWrapped(this, value[1]);
			return value;
		});
	},
	every(fn, thisArg) {
		return apply(this, "every", fn, thisArg, void 0, arguments);
	},
	filter(fn, thisArg) {
		return apply(this, "filter", fn, thisArg, (v) => v.map((item) => toWrapped(this, item)), arguments);
	},
	find(fn, thisArg) {
		return apply(this, "find", fn, thisArg, (item) => toWrapped(this, item), arguments);
	},
	findIndex(fn, thisArg) {
		return apply(this, "findIndex", fn, thisArg, void 0, arguments);
	},
	findLast(fn, thisArg) {
		return apply(this, "findLast", fn, thisArg, (item) => toWrapped(this, item), arguments);
	},
	findLastIndex(fn, thisArg) {
		return apply(this, "findLastIndex", fn, thisArg, void 0, arguments);
	},
	forEach(fn, thisArg) {
		return apply(this, "forEach", fn, thisArg, void 0, arguments);
	},
	includes(...args) {
		return searchProxy(this, "includes", args);
	},
	indexOf(...args) {
		return searchProxy(this, "indexOf", args);
	},
	join(separator) {
		return reactiveReadArray(this).join(separator);
	},
	lastIndexOf(...args) {
		return searchProxy(this, "lastIndexOf", args);
	},
	map(fn, thisArg) {
		return apply(this, "map", fn, thisArg, void 0, arguments);
	},
	pop() {
		return noTracking(this, "pop");
	},
	push(...args) {
		return noTracking(this, "push", args);
	},
	reduce(fn, ...args) {
		return reduce(this, "reduce", fn, args);
	},
	reduceRight(fn, ...args) {
		return reduce(this, "reduceRight", fn, args);
	},
	shift() {
		return noTracking(this, "shift");
	},
	some(fn, thisArg) {
		return apply(this, "some", fn, thisArg, void 0, arguments);
	},
	splice(...args) {
		return noTracking(this, "splice", args);
	},
	toReversed() {
		return reactiveReadArray(this).toReversed();
	},
	toSorted(comparer) {
		return reactiveReadArray(this).toSorted(comparer);
	},
	toSpliced(...args) {
		return reactiveReadArray(this).toSpliced(...args);
	},
	unshift(...args) {
		return noTracking(this, "unshift", args);
	},
	values() {
		return iterator(this, "values", (item) => toWrapped(this, item));
	}
};
function iterator(self, method, wrapValue) {
	const arr = shallowReadArray(self);
	const iter = arr[method]();
	if (arr !== self && !/* @__PURE__ */ isShallow(self)) {
		iter._next = iter.next.bind(iter);
		iter.next = () => {
			const result = iter._next();
			if (!result.done) result.value = wrapValue(result.value);
			return result;
		};
	}
	return iter;
}
const arrayProto = Array.prototype;
function apply(self, method, fn, thisArg, wrappedRetFn, args) {
	const arr = shallowReadArray(self);
	const needsWrap = arr !== self && !/* @__PURE__ */ isShallow(self);
	const methodFn = arr[method];
	if (methodFn !== arrayProto[method]) {
		const result = methodFn.apply(self, args);
		return needsWrap ? toReactive(result) : result;
	}
	let wrappedFn = fn;
	if (arr !== self) {
		if (needsWrap) wrappedFn = function(item, index) {
			return fn.call(this, toWrapped(self, item), index, self);
		};
		else if (fn.length > 2) wrappedFn = function(item, index) {
			return fn.call(this, item, index, self);
		};
	}
	const result = methodFn.call(arr, wrappedFn, thisArg);
	return needsWrap && wrappedRetFn ? wrappedRetFn(result) : result;
}
function reduce(self, method, fn, args) {
	const arr = shallowReadArray(self);
	const needsWrap = arr !== self && !/* @__PURE__ */ isShallow(self);
	let wrappedFn = fn;
	let wrapInitialAccumulator = false;
	if (arr !== self) {
		if (needsWrap) {
			wrapInitialAccumulator = args.length === 0;
			wrappedFn = function(acc, item, index) {
				if (wrapInitialAccumulator) {
					wrapInitialAccumulator = false;
					acc = toWrapped(self, acc);
				}
				return fn.call(this, acc, toWrapped(self, item), index, self);
			};
		} else if (fn.length > 3) wrappedFn = function(acc, item, index) {
			return fn.call(this, acc, item, index, self);
		};
	}
	const result = arr[method](wrappedFn, ...args);
	return wrapInitialAccumulator ? toWrapped(self, result) : result;
}
function searchProxy(self, method, args) {
	const arr = /* @__PURE__ */ toRaw(self);
	track(arr, "iterate", ARRAY_ITERATE_KEY);
	const res = arr[method](...args);
	if ((res === -1 || res === false) && /* @__PURE__ */ isProxy(args[0])) {
		args[0] = /* @__PURE__ */ toRaw(args[0]);
		return arr[method](...args);
	}
	return res;
}
function noTracking(self, method, args = []) {
	startBatch();
	const prevSub = setActiveSub();
	const res = (/* @__PURE__ */ toRaw(self))[method].apply(self, args);
	setActiveSub(prevSub);
	endBatch();
	return res;
}
//#endregion
//#region src/handlers.ts
const isNonTrackableKeys = /*@__PURE__*/ makeMap(`__proto__,__isLune`);
const builtInSymbols = new Set(/*@__PURE__*/ Object.getOwnPropertyNames(Symbol).filter((key) => key !== "arguments" && key !== "caller").map((key) => Symbol[key]).filter(isSymbol));
function hasOwnProperty(key) {
	if (!isSymbol(key)) key = String(key);
	const obj = /* @__PURE__ */ toRaw(this);
	track(obj, "has", key);
	return hasOwn(obj, key);
}
/**
* Intercepts reflective actions against observed targets, delegating `get` executions
* to dynamic dependency resolution pipelines and `set` mutations to the engine updater loop.
*/
var BaseReactiveHandler = class {
	_isReadonly;
	_isShallow;
	constructor(_isReadonly = false, _isShallow = false) {
		this._isReadonly = _isReadonly;
		this._isShallow = _isShallow;
	}
	/**
	* Trap for reading property values. Transparently handles nested proxy conversions,
	* skips untrackable keys, tracks reads, and overrides native behavior for array instrumentation.
	*/
	get(target, key, receiver) {
		if (key === "__lu_skip") return target["__lu_skip"];
		const isReadonly = this._isReadonly, isShallow = this._isShallow;
		switch (key) {
			case "__lu_isReactive": return !isReadonly;
			case "__lu_isReadonly": return isReadonly;
			case "__lu_isShallow": return isShallow;
			case "__lu_raw":
				if (receiver === (isReadonly ? isShallow ? shallowReadonlyMap : readonlyMap : isShallow ? shallowReactiveMap : reactiveMap).get(target) || Object.getPrototypeOf(target) === Object.getPrototypeOf(receiver)) return target;
				return;
		}
		if (!isReadonly) {
			let fn;
			if (isArray(target) && (fn = arrayInstrumentations[key])) return fn;
			if (key === "hasOwnProperty") return hasOwnProperty;
		}
		const res = Reflect.get(target, key, receiver);
		if (isSymbol(key) ? builtInSymbols.has(key) : isNonTrackableKeys(key)) return res;
		if (!isReadonly) track(target, "get", key);
		if (isShallow) return res;
		if (isObject(res)) return isReadonly ? /* @__PURE__ */ readonly(res) : /* @__PURE__ */ reactive(res);
		return res;
	}
};
var MutableReactiveHandler = class extends BaseReactiveHandler {
	constructor(isShallow = false) {
		super(false, isShallow);
	}
	set(target, key, value, receiver) {
		let oldValue = target[key];
		const isArrayWithIntegerKey = isArray(target) && isIntegerKey(key);
		if (!this._isShallow) {
			if (!/* @__PURE__ */ isShallow(value) && !/* @__PURE__ */ isReadonly(value)) {
				oldValue = /* @__PURE__ */ toRaw(oldValue);
				value = /* @__PURE__ */ toRaw(value);
			}
		}
		const hadKey = isArrayWithIntegerKey ? Number(key) < target.length : hasOwn(target, key);
		const result = Reflect.set(target, key, value, receiver);
		if (target === /* @__PURE__ */ toRaw(receiver) && result) {
			if (!hadKey) trigger(target, "add", key, value);
			else if (hasChanged(value, oldValue)) trigger(target, "set", key, value, oldValue);
		}
		return result;
	}
	deleteProperty(target, key) {
		const hadKey = hasOwn(target, key);
		const oldValue = target[key];
		const result = Reflect.deleteProperty(target, key);
		if (result && hadKey) trigger(target, "delete", key, void 0, oldValue);
		return result;
	}
	has(target, key) {
		const result = Reflect.has(target, key);
		if (!isSymbol(key) || !builtInSymbols.has(key)) track(target, "has", key);
		return result;
	}
	ownKeys(target) {
		track(target, "iterate", isArray(target) ? "length" : ITERATE_KEY);
		return Reflect.ownKeys(target);
	}
};
var ReadonlyReactiveHandler = class extends BaseReactiveHandler {
	constructor(isShallow = false) {
		super(true, isShallow);
	}
	set(target, key) {
		if (import.meta.env.DEV) warn(`Set operation on key "${String(key)}" failed: target is readonly.`, target);
		return true;
	}
	deleteProperty(target, key) {
		if (import.meta.env.DEV) warn(`Delete operation on key "${String(key)}" failed: target is readonly.`, target);
		return true;
	}
};
const mutableHandlers = /*@__PURE__*/ new MutableReactiveHandler();
const readonlyHandlers = /*@__PURE__*/ new ReadonlyReactiveHandler();
const shallowReactiveHandlers = /*@__PURE__*/ new MutableReactiveHandler(true);
const shallowReadonlyHandlers = /*@__PURE__*/ new ReadonlyReactiveHandler(true);
//#endregion
//#region src/reactive.ts
const reactiveMap = /* @__PURE__ */ new WeakMap();
const readonlyMap = /* @__PURE__ */ new WeakMap();
const shallowReactiveMap = /* @__PURE__ */ new WeakMap();
const shallowReadonlyMap = /* @__PURE__ */ new WeakMap();
function createReactiveObject(target, isReadonly, handler, proxyMap) {
	if (!isObject(target)) {
		if (import.meta.env.DEV) warn(`value cannot be made ${isReadonly ? "readonly" : "reactive"}: ${String(target)}`);
		return target;
	}
	if (target["__lu_raw"] && !(isReadonly && target["__lu_isReactive"])) return target;
	if (target["__lu_skip"] || !Object.isExtensible(target)) return target;
	const existingProxy = proxyMap.get(target);
	if (existingProxy) return existingProxy;
	if (targetTypeMap(toRawType(target)) === 0) return target;
	const proxy = new Proxy(target, handler);
	proxyMap.set(target, proxy);
	return proxy;
}
/*@__NO_SIDE_EFFECTS__*/
function reactive(target) {
	if (/* @__PURE__ */ isReadonly(target)) return target;
	return createReactiveObject(target, false, mutableHandlers, reactiveMap);
}
/**
* Creates a shallow reactive proxy where only top-level properties trigger tracking and reactive updates.
* Nested objects inside the properties remain un-proxied raw values.
* @param target - The source object or array.
* @returns A shallow reactive proxy.
* @see {@link http://lune-js.com/guide/reactivity#shallowreactive-object-shallowreadonly-object}
*/
/*@__NO_SIDE_EFFECTS__*/
function shallowReactive(target) {
	return createReactiveObject(target, false, shallowReactiveHandlers, shallowReactiveMap);
}
/**
* Creates a deeply read-only proxy. Attempts to set properties or trigger structural
* mutations emit warnings in development mode and fail gracefully.
* @param target - The source object to make read-only.
* @returns A deeply immutable and reactive proxy window.
* @see {@link http://lune-js.com/guide/reactivity#readonly-object}
*/
/*@__NO_SIDE_EFFECTS__*/
function readonly(target) {
	return createReactiveObject(target, true, readonlyHandlers, readonlyMap);
}
/**
* Shallow version of {@link readonly}.
*
* Unlike {@link readonly}, there is no deep conversion: only root-level
* properties are made readonly. Property values are stored and exposed as-is -
* this also means properties with ref values will not be automatically
* unwrapped.
*
* @example
* ```js
* const state = shallowReadonly({
*   foo: 1,
*   nested: {
*     bar: 2
*   }
* })
*
* // mutating state's own properties will fail
* state.foo++
*
* // ...but works on nested objects
* isReadonly(state.nested) // false
*
* // works
* state.nested.bar++
* ```
*
* @param target - The source object.
* @see {@link http://lune-js.com/guide/reactivity#shallowreactive-object-shallowreadonly-object}
*/
/*@__NO_SIDE_EFFECTS__*/
function shallowReadonly(target) {
	return createReactiveObject(target, true, shallowReadonlyHandlers, shallowReadonlyMap);
}
function targetTypeMap(rawType) {
	switch (rawType) {
		case "Object":
		case "Array": return 1;
		default: return 0;
	}
}
//#endregion
export { ReactiveEffect, cleanup, effect, isIntegerKey, isProxy, isReactive, isReadonly, isShallow, makeMap, reactive, reactiveMap, readonly, readonlyMap, shallowReactive, shallowReactiveMap, shallowReadonly, shallowReadonlyMap, stop, toRaw, toRawType };
